import { ScrollView } from "react-native";
import { CalendarCreateScreenProps } from "./CalendarCreateNavigation";
import { AHSeparator, ListItem, useBoxStyle } from "arkh";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import React, { useCallback, useLayoutEffect } from "react";
import { CalendarAddMutation } from "./__generated__/CalendarAddMutation.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { CalendarAddQuery } from "./__generated__/CalendarAddQuery.graphql";
import { useAlert } from "@/src/common/Alert";
import { HeaderButtonLoading } from "@/src/components";

export function CalendarAddScreen(
  props: CalendarCreateScreenProps<"CalendarAddScreen">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <CalendarAddScreenComponent {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function CalendarAddScreenComponent(
  props: CalendarCreateScreenProps<"CalendarAddScreen"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<CalendarAddQuery>(
    graphql`
      query CalendarAddQuery {
        store {
          calendarAddSuggestions {
            title
            iCalUrl
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );
  const intl_tokens = useIntlTokens();
  const [subscribeToCalendar, subscribeToCalendarLoading] =
    useMutation<CalendarAddMutation>(graphql`
      mutation CalendarAddMutation($input: StoreCalendarSubscribeInput!) {
        store {
          subscribeToCalendar(input: $input) {
            id
            store {
              calendarAddSuggestions {
                iCalUrl
                title
              }
            }
          }
        }
      }
    `);

  const alert = useAlert();

  const onSubscribeToCalendar = useCallback(
    (url: string, calendarName: string) => {
      subscribeToCalendar({
        variables: {
          input: {
            url,
            calendarName,
          },
        },
        onCompleted: (response) => {
          props.navigation.replace("CalendarEdit", {
            calendarId: response.store.subscribeToCalendar.id,
            fromSubscribeToCalendar: true,
          });
        },
        onError: (error) => {
          alert.error(
            "Fel Uppstod",
            `Misslyckades att prenumerera på ${calendarName}`
          );
        },
      });
    },
    [subscribeToCalendar, props.navigation, alert]
  );

  const insets = useSafeAreaInsets();

  const contentContainerStyle = useBoxStyle({
    space: "medium",
    insets: {
      bottom: insets.bottom,
    },
  });

  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions({
      ...getNavigationHeaderOptions({
        type: "CANCEL",
        onClose: () => props.navigation.goBack(),
        title: intl_tokens.AddCalendar,
        defaultContentStyle: true,
      }),
      headerRight: () =>
        subscribeToCalendarLoading ? <HeaderButtonLoading /> : null,
    });
  }, [
    getNavigationHeaderOptions,
    intl_tokens.AddCalendar,
    props.navigation,
    subscribeToCalendarLoading,
  ]);

  return (
    <ScrollView contentContainerStyle={contentContainerStyle}>
      {data.store.calendarAddSuggestions.map((c, index) => {
        return (
          <React.Fragment key={c.iCalUrl}>
            {index > 0 ? <AHSeparator /> : null}
            <ListItem
              right={<ListItem.AddButton />}
              isFirst={index === 0}
              isLast={index === data.store.calendarAddSuggestions.length - 1}
              border="border-primary"
              disabled={subscribeToCalendarLoading}
              onPress={() => {
                onSubscribeToCalendar(c.iCalUrl, c.title);
              }}
            >
              <ListItem.Title>{c.title}</ListItem.Title>
            </ListItem>
          </React.Fragment>
        );
      })}
      {data.store.calendarAddSuggestions.length > 0 ? (
        <AHSeparator spaceVertical="medium" />
      ) : null}
      <ListItem
        isLast
        isFirst
        border="border-primary"
        right={<ListItem.NavArrow />}
        disabled={subscribeToCalendarLoading}
        onPress={() => {
          props.navigation.navigate("CalendarSubscribeScreen");
        }}
      >
        <ListItem.Title>
          <FormattedMessage
            defaultMessage="Subscribe to an external calendar"
            description="CalendarSubscribe URL mode title"
          />
        </ListItem.Title>
      </ListItem>
      <AHSeparator spaceVertical="medium" />
      <ListItem
        isLast
        isFirst
        disabled={subscribeToCalendarLoading}
        border="border-primary"
        right={<ListItem.NavArrow />}
        onPress={() => {
          props.navigation.navigate("CalendarCreateScreen");
        }}
      >
        <ListItem.Title>{intl_tokens.CreateCalendar}</ListItem.Title>
      </ListItem>
    </ScrollView>
  );
}
